import React from 'react';
import { services } from '../mock';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Services = ({ onQuoteClick }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Comprehensive solutions from consultation to installation and
              beyond
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = require('lucide-react')[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 group hover:bg-white border border-transparent hover:border-blue-100"
                >
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-blue-900 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="text-white" size={32} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <span className="text-blue-900 font-bold text-lg mr-3">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {service.title}
                        </h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A streamlined approach ensuring quality at every step
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200" />

            {/* Timeline Items */}
            <div className="space-y-12">
              {/* Step 1 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12 lg:text-right mb-8 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Initial Consultation
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      We begin with understanding your requirements, budget, and
                      vision. Our experts provide professional recommendations
                      tailored to your needs.
                    </p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 hidden lg:flex">
                  1
                </div>
                <div className="lg:w-1/2 lg:pl-12" />
              </div>

              {/* Step 2 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12" />
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 hidden lg:flex">
                  2
                </div>
                <div className="lg:w-1/2 lg:pl-12 mb-8 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Site Survey & Measurement
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our team visits your location to take precise measurements
                      and assess site conditions, ensuring perfect fit and
                      functionality.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12 lg:text-right mb-8 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Design & Approval
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      We create custom designs and provide detailed quotations.
                      Once approved, we proceed with material selection and
                      order processing.
                    </p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 hidden lg:flex">
                  3
                </div>
                <div className="lg:w-1/2 lg:pl-12" />
              </div>

              {/* Step 4 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12" />
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 hidden lg:flex">
                  4
                </div>
                <div className="lg:w-1/2 lg:pl-12 mb-8 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Manufacturing
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Products are manufactured in our state-of-the-art facility
                      with stringent quality control at every stage of
                      production.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12 lg:text-right mb-8 lg:mb-0">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Professional Installation
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our skilled installation team ensures precise fitting and
                      seamless integration with your space, with minimal
                      disruption to your routine.
                    </p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 hidden lg:flex">
                  5
                </div>
                <div className="lg:w-1/2 lg:pl-12" />
              </div>

              {/* Step 6 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12" />
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-lg z-10 hidden lg:flex">
                  6
                </div>
                <div className="lg:w-1/2 lg:pl-12">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      After-Sales Support
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      We provide comprehensive warranty coverage, maintenance
                      guidance, and ongoing support to ensure long-lasting
                      satisfaction.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Services?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <ArrowRight className="text-blue-900" size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Expert Team
                    </h3>
                    <p className="text-gray-600">
                      Experienced professionals with deep industry knowledge and
                      technical expertise.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <ArrowRight className="text-blue-900" size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Timely Delivery
                    </h3>
                    <p className="text-gray-600">
                      We respect your time and ensure projects are completed
                      within agreed timelines.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <ArrowRight className="text-blue-900" size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Quality Guarantee
                    </h3>
                    <p className="text-gray-600">
                      Comprehensive warranty coverage and quality assurance on
                      all products and services.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <ArrowRight className="text-blue-900" size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Transparent Pricing
                    </h3>
                    <p className="text-gray-600">
                      No hidden costs. Clear, detailed quotations that help you
                      make informed decisions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1704040686684-be330b48412e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NjkxMDMzNjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional Service"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Schedule a free consultation with our experts today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onQuoteClick}
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-8 py-6"
            >
              Request a Quote
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;